import { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  CandlestickData,
  Time,
  ColorType,
  CandlestickSeries,
  HistogramSeries,
  SeriesMarker,
  createSeriesMarkers,
  ISeriesMarkersPluginApi,
  HistogramData,
} from 'lightweight-charts';
import { KlineCandle } from '../services/klineTypes';
import { klineDataService } from '../services/klineDataService';
import { ThemeMode, BarInterval } from '@/services/enums';

// --- 数据转换辅助方法 ---

const toCandlestickData = (c: KlineCandle): CandlestickData => ({
  time: c.time as Time,
  open: c.open,
  high: c.high,
  low: c.low,
  close: c.close,
});

const toVolumeData = (c: KlineCandle): HistogramData => ({
  time: c.time as Time,
  value: c.volume,
  color: c.close >= c.open ? '#26a69a80' : '#ef535080',
});

const toCandlestickDataArray = (candles: KlineCandle[]): CandlestickData[] => candles.map(toCandlestickData);

const toVolumeDataArray = (candles: KlineCandle[]): HistogramData[] => candles.map(toVolumeData);

export interface KlineChartProps {
  symbol: string;
  bar: BarInterval;
  theme?: ThemeMode;
  candles?: KlineCandle[]; // 可选，如果提供则使用外部数据，否则自动加载
  showVolume?: boolean;
  autoScroll?: boolean;
  markers?: SeriesMarker<Time>[];
  height?: number | string;
  onVisibleTimeRangeChange?: (range: { from: number; to: number }) => void;
  // 自动加载选项
  autoLoad?: boolean; // 是否自动加载，默认为true（当candles未提供时）
  loadOptions?: {
    start?: number; // 开始时间戳（秒）
    end?: number; // 结束时间戳（秒）
    limit?: number; // 最大返回数量，默认200
  };
  onLoad?: (candles: KlineCandle[]) => void; // 加载完成回调
  onError?: (error: string) => void; // 错误回调
  loading?: boolean; // 外部loading状态（可选）
}

const formatPrice = (price: number) => {
  const absPrice = Math.abs(price);
  if (absPrice === 0) return '0.00';
  if (absPrice >= 100) return price.toFixed(2);
  if (absPrice >= 1) return price.toFixed(2);
  if (absPrice >= 0.1) return price.toFixed(4);
  if (absPrice >= 0.01) return price.toFixed(5);
  if (absPrice >= 0.001) return price.toFixed(6);
  return price.toFixed(8);
};

export interface KlineChartRef {
  updateCandle: (candle: KlineCandle) => void;
  setData: (candles: KlineCandle[]) => void;
  scrollToRealTime: () => void;
  fitContent: () => void;
  getChart: () => IChartApi | null;
  setVisibleRange: (range: { from: number; to: number }) => void;
}

const KlineChart = forwardRef<KlineChartRef, KlineChartProps>(
  (
    {
      symbol,
      bar,
      theme = 'dark',
      candles: externalCandles,
      showVolume = true,
      autoScroll = true,
      markers = [],
      height = '50vh',
      onVisibleTimeRangeChange,
      autoLoad = true,
      loadOptions,
      onLoad,
      onError,
      loading,
    },
    ref,
  ) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const candleSeriesRef = useRef<ISeriesApi<'Candlestick', Time> | null>(null);
    const volumeSeriesRef = useRef<ISeriesApi<'Histogram', Time> | null>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candlesRef = useRef<KlineCandle[]>([]); // 全量数据数组（使用 ref 避免闭包陷阱）
    const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
    const barRef = useRef<BarInterval>(bar);
    const symbolRef = useRef<string>(symbol);

    // 加载状态锁（使用 ref 避免闭包陷阱）
    const isLoadingRef = useRef<boolean>(false);
    const earliestTimestampRef = useRef<number | null>(null); // 已加载的最早时间戳
    const logicalRangeHandlerRef = useRef<
      ((logicalRange: { from: number | null; to: number | null } | null) => void) | null
    >(null);
    const isInitializedRef = useRef<boolean>(false); // 标记图表是否已初始化
    const hasEmptyDataRef = useRef<boolean>(false); // 标记是否已经尝试加载但返回空数据（避免重复请求）
    const lastLoadKeyRef = useRef<string>(''); // 记录上次加载的 symbol+bar 组合
    const timeRangeLoadingRef = useRef<boolean>(false); // 标记时间范围是否正在加载（避免重复请求）
    const lastTimeRangeKeyRef = useRef<string>(''); // 记录上次请求时间范围的 symbol+bar 组合
    const isSettingVisibleRangeRef = useRef<boolean>(false); // 标记是否正在设置视口范围（防止触发懒加载）
    const loadKlineDataRef = useRef<typeof loadKlineData | null>(null); // 保存 loadKlineData 函数引用
    const pendingVisibleRangeRef = useRef<{ candles: KlineCandle[]; sym: string; interval: BarInterval } | null>(null); // 待设置的视口范围
    const isInitialDataLoadRef = useRef<boolean>(false); // 标记是否是初始数据加载（防止初始加载后触发懒加载）

    // 内部状态
    const [internalCandles, setInternalCandles] = useState<KlineCandle[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [internalError, setInternalError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState<{ earliest: number; latest: number } | null>(null);

    // 更新 refs
    useEffect(() => {
      barRef.current = bar;
      symbolRef.current = symbol;
    }, [bar, symbol]);

    // 决定使用外部数据还是内部数据
    const shouldAutoLoad = autoLoad && !externalCandles;
    const candles = externalCandles || internalCandles;
    const isAnyLoading = loading !== undefined ? loading : internalLoading;

    // 设置视口时间范围的辅助函数（使用 ref 保存，避免依赖问题）
    const setVisibleRangeAfterLoadRef = useRef<
      ((candles: KlineCandle[], sym: string, interval: BarInterval) => void) | null
    >(null);

    const setVisibleRangeAfterLoad = useCallback((candles: KlineCandle[], sym: string, interval: BarInterval) => {
      if (!chartRef.current || candles.length === 0) return;

      try {
        const cacheKey = `kline_visible_range_${sym}_${interval}`;
        const cached = localStorage.getItem(cacheKey);
        let shouldUseCache = false;
        let cachedFrom = 0;
        let cachedTo = 0;

        if (cached) {
          const cacheData = JSON.parse(cached);
          // 检查缓存是否过期（7天）
          const cacheTime = cacheData.timestamp || 0;
          const now = Date.now();
          const sevenDays = 7 * 24 * 60 * 60 * 1000;

          if (now - cacheTime < sevenDays && cacheData.from && cacheData.to) {
            // 确保时间范围在K线数据范围内
            const earliestTime = candles[0]?.time || 0;
            const latestTime = candles[candles.length - 1]?.time || 0;
            cachedFrom = Math.max(cacheData.from, earliestTime);
            cachedTo = Math.min(cacheData.to, latestTime);

            if (cachedFrom < cachedTo) {
              shouldUseCache = true;
            }
          } else {
            // 缓存过期，删除
            localStorage.removeItem(cacheKey);
          }
        }

        if (shouldUseCache) {
          // 使用缓存设置视口
          console.log('[KlineChart] 从缓存恢复K线视口时间范围', { from: cachedFrom, to: cachedTo });
          isSettingVisibleRangeRef.current = true;
          chartRef.current.timeScale().setVisibleRange({
            from: cachedFrom as Time,
            to: cachedTo as Time,
          });
          // 延迟重置标记，确保设置视口的事件处理完成
          setTimeout(() => {
            isSettingVisibleRangeRef.current = false;
          }, 500);
        } else {
          // 没有缓存，显示最后200条K线的时间范围
          const lastCandles = candles.slice(-200);
          if (lastCandles.length > 0) {
            const firstTime =
              typeof lastCandles[0].time === 'number' ? lastCandles[0].time : parseInt(String(lastCandles[0].time));
            const lastTime =
              typeof lastCandles[lastCandles.length - 1].time === 'number'
                ? lastCandles[lastCandles.length - 1].time
                : parseInt(String(lastCandles[lastCandles.length - 1].time));

            console.log('[KlineChart] 设置视口为最后200条K线时间范围', { from: firstTime, to: lastTime });
            isSettingVisibleRangeRef.current = true;
            chartRef.current.timeScale().setVisibleRange({
              from: firstTime as Time,
              to: lastTime as Time,
            });
            // 延迟重置标记，确保设置视口的事件处理完成
            setTimeout(() => {
              isSettingVisibleRangeRef.current = false;
            }, 500);
          }
        }
      } catch (error) {
        console.error('[KlineChart] 设置视口时间范围失败:', error);
        // 出错时，尝试显示最后200条
        const lastCandles = candles.slice(-200);
        if (lastCandles.length > 0 && chartRef.current) {
          const firstTime =
            typeof lastCandles[0].time === 'number' ? lastCandles[0].time : parseInt(String(lastCandles[0].time));
          const lastTime =
            typeof lastCandles[lastCandles.length - 1].time === 'number'
              ? lastCandles[lastCandles.length - 1].time
              : parseInt(String(lastCandles[lastCandles.length - 1].time));

          isSettingVisibleRangeRef.current = true;
          chartRef.current.timeScale().setVisibleRange({
            from: firstTime as Time,
            to: lastTime as Time,
          });
          // 延迟重置标记，确保设置视口的事件处理完成
          setTimeout(() => {
            isSettingVisibleRangeRef.current = false;
          }, 500);
        }
      }
    }, []);

    // 更新 setVisibleRangeAfterLoad 的 ref
    useEffect(() => {
      setVisibleRangeAfterLoadRef.current = setVisibleRangeAfterLoad;
    }, [setVisibleRangeAfterLoad]);

    // 将height属性转换为像素值（辅助函数）
    const parseHeight = (height: number | string): number => {
      if (typeof height === 'number') {
        return height;
      }
      // 处理vh单位
      if (height.endsWith('vh')) {
        const vhValue = parseFloat(height);
        return window.innerHeight * (vhValue / 100);
      }
      // 处理px单位或其他数字字符串
      const parsed = parseInt(height);
      return isNaN(parsed) ? window.innerHeight * 0.5 : parsed;
    };

    // 自动加载K线数据（初始加载或加载更多历史数据）
    const loadKlineData = useCallback(
      async (sym: string, interval: BarInterval, loadHistory: boolean = false, forceRefresh: boolean = false) => {
        if (!sym || isLoadingRef.current) {
          return;
        }

        // 如果是强制刷新，即使有外部数据也允许加载
        if (!forceRefresh && externalCandles && !autoLoad) {
          console.log('[KlineChart] 有外部数据且autoLoad为false，跳过内部加载');
          return;
        }

        // 检查是否已经尝试过加载但返回空数据（避免重复请求）
        const currentLoadKey = `${sym}-${interval}`;
        if (!loadHistory && hasEmptyDataRef.current && lastLoadKeyRef.current === currentLoadKey) {
          console.log('[KlineChart] 已尝试加载但返回空数据，跳过重复请求', { sym, interval });
          return;
        }

        isLoadingRef.current = true;
        setInternalLoading(true);
        setInternalError(null);

        try {
          let data;

          if (loadHistory && earliestTimestampRef.current) {
            // 加载历史数据：从最早时间戳往前加载
            const limit = loadOptions?.limit || 200;

            data = await klineDataService.getKlineData(sym, interval, {
              end: earliestTimestampRef.current - 1, // 不包含当前最早时间戳，避免重复
              limit,
            });

            if (data.candles.length === 0) {
              // 没有更多历史数据了
              isLoadingRef.current = false;
              setInternalLoading(false);
              return;
            }

            // 将历史数据拼接到数组头部
            const newCandles = [...data.candles, ...candlesRef.current];
            candlesRef.current = newCandles;

            // 更新最早时间戳
            if (data.candles.length > 0) {
              const firstCandle = data.candles[0];
              const firstTime =
                typeof firstCandle.time === 'number' ? firstCandle.time : parseInt(String(firstCandle.time));
              earliestTimestampRef.current = firstTime;
            }

            // 更新状态（触发重渲染）
            setInternalCandles(newCandles);
          } else {
            // 初始加载
            const endTime = loadOptions?.end || Math.floor(Date.now() / 1000);
            const limit = loadOptions?.limit || 200;

            data = await klineDataService.getKlineData(sym, interval, {
              end: endTime,
              limit,
            });

            if (data.candles.length === 0) {
              const errorMsg = '该币种暂无K线数据';
              setInternalError(errorMsg);
              if (onError) {
                onError(errorMsg);
              }
              candlesRef.current = [];
              setInternalCandles([]);
              // 标记已尝试加载但返回空数据，避免重复请求
              hasEmptyDataRef.current = true;
              lastLoadKeyRef.current = currentLoadKey;
              // 重置加载状态
              isLoadingRef.current = false;
              setInternalLoading(false);
              return;
            }

            // 成功加载数据，重置空数据标记
            hasEmptyDataRef.current = false;
            lastLoadKeyRef.current = currentLoadKey;

            candlesRef.current = data.candles;

            // 记录最早时间戳
            if (data.candles.length > 0) {
              const firstCandle = data.candles[0];
              const firstTime =
                typeof firstCandle.time === 'number' ? firstCandle.time : parseInt(String(firstCandle.time));
              earliestTimestampRef.current = firstTime;
            }

            setInternalCandles(data.candles);

            // 标记这是初始数据加载（不是历史数据懒加载）
            if (!loadHistory) {
              isInitialDataLoadRef.current = true;
            }

            // 保存待设置的视口范围，等数据设置到图表后再设置
            if (data.candles.length > 0 && !loadHistory) {
              pendingVisibleRangeRef.current = {
                candles: data.candles,
                sym,
                interval,
              };
            }

            if (onLoad) {
              onLoad(data.candles);
            }
          }
        } catch (err: any) {
          const errorMsg = err.message || '加载K线数据失败';
          setInternalError(errorMsg);
          if (onError) {
            onError(errorMsg);
          }
          if (!loadHistory) {
            candlesRef.current = [];
            setInternalCandles([]);
          }
        } finally {
          isLoadingRef.current = false;
          setInternalLoading(false);
        }
      },
      [loadOptions, onLoad, onError, externalCandles, autoLoad],
    );

    // 更新 loadKlineData 的 ref，确保总是使用最新的函数
    useEffect(() => {
      loadKlineDataRef.current = loadKlineData;
    }, [loadKlineData]);

    // 当需要自动加载时，监听symbol和bar的变化
    useEffect(() => {
      // 当 symbol 或 bar 变化时，重置空数据标记和初始加载标记
      const currentLoadKey = `${symbol}-${bar}`;
      if (lastLoadKeyRef.current !== currentLoadKey) {
        hasEmptyDataRef.current = false;
        lastLoadKeyRef.current = currentLoadKey;
        // 切换 symbol 或 bar 时，重置初始加载标记，允许新的初始加载
        isInitialDataLoadRef.current = false;
      }

      // 如果正在加载中，跳过重复请求
      if (isLoadingRef.current) {
        console.log('[KlineChart] 正在加载中，跳过重复请求', { symbol, bar });
        return;
      }

      if (shouldAutoLoad && symbol && loadKlineDataRef.current) {
        loadKlineDataRef.current(symbol, bar);
      }
    }, [shouldAutoLoad, symbol, bar]);

    // 更新 bar ref
    useEffect(() => {
      barRef.current = bar;
    }, [bar]);

    // 获取K线数据的时间范围
    useEffect(() => {
      if (!symbol || !bar) {
        setTimeRange(null);
        lastTimeRangeKeyRef.current = '';
        return;
      }

      // 检查是否正在加载或已经加载过相同的数据
      const currentTimeRangeKey = `${symbol}-${bar}`;
      if (timeRangeLoadingRef.current && lastTimeRangeKeyRef.current === currentTimeRangeKey) {
        console.log('[KlineChart] 时间范围正在加载中，跳过重复请求', { symbol, bar });
        return;
      }

      // 如果已经加载过相同的数据，不再重复请求
      if (lastTimeRangeKeyRef.current === currentTimeRangeKey) {
        console.log('[KlineChart] 时间范围已加载，跳过重复请求', { symbol, bar });
        return;
      }

      const fetchTimeRange = async () => {
        timeRangeLoadingRef.current = true;
        lastTimeRangeKeyRef.current = currentTimeRangeKey;

        try {
          const range = await klineDataService.getKlineTimeRange(symbol, bar);
          console.log('[KlineChart] 获取到时间范围:', range);
          if (range.hasData && range.earliestTime && range.latestTime) {
            setTimeRange({
              earliest: range.earliestTime,
              latest: range.latestTime,
            });
            console.log('[KlineChart] 设置时间范围:', { earliest: range.earliestTime, latest: range.latestTime });
          } else {
            console.log('[KlineChart] 无数据或时间范围无效');
            setTimeRange(null);
          }
        } catch (error: any) {
          console.error('[KlineChart] 获取K线时间范围失败:', error);
          setTimeRange(null);
        } finally {
          timeRangeLoadingRef.current = false;
        }
      };

      fetchTimeRange();
    }, [symbol, bar]);

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      updateCandle: (candle: KlineCandle) => {
        if (candleSeriesRef.current) {
          candleSeriesRef.current.update(toCandlestickData(candle));
        }
        if (volumeSeriesRef.current && showVolume) {
          volumeSeriesRef.current.update(toVolumeData(candle));
        }
      },
      setData: (newCandles: KlineCandle[]) => {
        if (candleSeriesRef.current) {
          candleSeriesRef.current.setData(toCandlestickDataArray(newCandles));
        }
        if (volumeSeriesRef.current) {
          if (showVolume) {
            volumeSeriesRef.current.setData(toVolumeDataArray(newCandles));
          } else {
            volumeSeriesRef.current.setData([]);
          }
        }
      },
      scrollToRealTime: () => {
        if (chartRef.current && autoScroll) {
          chartRef.current.timeScale().scrollToRealTime();
        }
      },
      fitContent: () => {
        if (chartRef.current) {
          chartRef.current.timeScale().fitContent();
        }
      },
      getChart: () => chartRef.current,
      setVisibleRange: (range: { from: number; to: number }) => {
        if (chartRef.current) {
          chartRef.current.timeScale().setVisibleRange({
            from: range.from as Time,
            to: range.to as Time,
          });
        }
      },
    }));

    // 初始化图表
    useEffect(() => {
      // 如果已经初始化，只更新主题和尺寸，不重新创建
      if (isInitializedRef.current && chartRef.current) {
        const chart = chartRef.current;
        // 更新主题
        chart.applyOptions({
          layout: {
            background: {
              type: ColorType.Solid,
              color: theme === 'dark' ? '#1a1a1a' : '#ffffff',
            },
            textColor: theme === 'dark' ? '#ffffff' : '#000000',
          },
          grid: {
            vertLines: { color: theme === 'dark' ? '#2a2a2a' : '#e0e0e0' },
            horzLines: { color: theme === 'dark' ? '#2a2a2a' : '#e0e0e0' },
          },
        });

        // 更新尺寸
        if (chartContainerRef.current) {
          const container = chartContainerRef.current;
          const rect = container.getBoundingClientRect();
          const width = rect.width || container.clientWidth || window.innerWidth;
          const heightValue = parseHeight(height);
          chart.applyOptions({ width, height: heightValue });
        }
        return;
      }

      // 如果未初始化，执行完整的初始化流程
      const initTimer = setTimeout(() => {
        if (!chartContainerRef.current) {
          return;
        }

        const container = chartContainerRef.current;
        const rect = container.getBoundingClientRect();
        const width = rect.width || container.clientWidth || window.innerWidth;
        const heightValue = parseHeight(height);

        if (width === 0 || heightValue === 0) {
          initializeChart(window.innerWidth, window.innerHeight * 0.5);
        } else {
          initializeChart(width, heightValue);
        }
      }, 100);

      function initializeChart(containerWidth: number, containerHeight: number) {
        if (!chartContainerRef.current) {
          return;
        }

        // 清理已有图表
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
          candleSeriesRef.current = null;
          volumeSeriesRef.current = null;
          markersPluginRef.current = null;
          isInitializedRef.current = false;
        }

        const container = chartContainerRef.current;

        try {
          const chart = createChart(container, {
            layout: {
              background: {
                type: ColorType.Solid,
                color: theme === 'dark' ? '#1a1a1a' : '#ffffff',
              },
              textColor: theme === 'dark' ? '#ffffff' : '#000000',
            },
            width: containerWidth,
            height: containerHeight,
            grid: {
              vertLines: { color: theme === 'dark' ? '#2a2a2a' : '#e0e0e0' },
              horzLines: { color: theme === 'dark' ? '#2a2a2a' : '#e0e0e0' },
            },
            crosshair: {
              mode: 1,
            },
            rightPriceScale: {
              borderColor: theme === 'dark' ? '#485058' : '#d1d4dc',
              visible: false,
            },
            leftPriceScale: {
              borderColor: theme === 'dark' ? '#485058' : '#d1d4dc',
              visible: true,
            },
            timeScale: {
              borderColor: theme === 'dark' ? '#485058' : '#d1d4dc',
              rightOffset: 10,
              visible: true,
              timeVisible: true,
              secondsVisible: false,
              fixLeftEdge: false,
              fixRightEdge: false,
            },
            localization: {
              locale: 'zh-CN',
              dateFormat: 'yyyy-MM-dd',
            },
          });

          const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#26a69a',
            downColor: '#ef5350',
            borderVisible: false,
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
            priceScaleId: 'left',
            priceFormat: {
              type: 'custom',
              formatter: formatPrice,
              minMove: 0.00000001,
            },
          });

          chart.priceScale('left').applyOptions({
            scaleMargins: {
              top: 0.05,
              bottom: 0.25,
            },
          });

          const volumeSeries = chart.addSeries(HistogramSeries, {
            color: '#26a69a33',
            priceFormat: {
              type: 'volume',
            },
            priceScaleId: 'volume',
          });

          chart.priceScale('volume').applyOptions({
            scaleMargins: {
              top: 0.8,
              bottom: 0,
            },
          });

          // 创建标记插件
          markersPluginRef.current = createSeriesMarkers(candleSeries, []);

          chartRef.current = chart;
          candleSeriesRef.current = candleSeries;
          volumeSeriesRef.current = volumeSeries;
          isInitializedRef.current = true;

          console.log('[KlineChart] 图表初始化完成', {
            hasCandles: candles.length > 0,
            candleCount: candles.length,
          });

          // 如果初始化时已经有数据，立即设置数据
          if (candles.length > 0) {
            candleSeries.setData(toCandlestickDataArray(candles));

            if (volumeSeries && showVolume) {
              volumeSeries.setData(toVolumeDataArray(candles));
            }

            setTimeout(() => {
              if (chart) {
                chart.timeScale().fitContent();
                if (autoScroll) {
                  chart.timeScale().scrollToRealTime();
                }
              }
            }, 100);
          }

          // 监听可见时间范围变化
          chart.timeScale().subscribeVisibleTimeRangeChange((timeRange) => {
            if (!timeRange) {
              return;
            }

            const { from, to } = timeRange;
            if (!from || !to) {
              return;
            }

            const fromTime = typeof from === 'number' ? from : parseInt(String(from));
            const toTime = typeof to === 'number' ? to : parseInt(String(to));

            // 通知父组件可见时间范围变化
            if (onVisibleTimeRangeChange) {
              onVisibleTimeRangeChange({ from: fromTime, to: toTime });
            }
          });

          // 监听逻辑范围变化（用于历史数据加载）
          const logicalRangeHandler = (logicalRange: { from: number | null; to: number | null } | null) => {
            if (!logicalRange || !shouldAutoLoad || !chartRef.current) {
              console.log('[KlineChart] 懒加载检查跳过：逻辑范围无效或不需要自动加载', {
                hasLogicalRange: !!logicalRange,
                shouldAutoLoad,
                hasChart: !!chartRef.current,
              });
              return;
            }

            // 如果正在设置视口范围，跳过懒加载检查
            if (isSettingVisibleRangeRef.current) {
              console.log('[KlineChart] 懒加载检查跳过：正在设置视口范围');
              return;
            }

            // 如果是初始数据加载后，暂时禁用懒加载
            if (isInitialDataLoadRef.current) {
              console.log('[KlineChart] 懒加载检查跳过：初始数据加载后，暂时禁用懒加载');
              return;
            }

            const { from } = logicalRange;

            // 当可视区域最左侧索引小于阈值（10）时，触发加载历史数据
            const threshold = 10;
            console.log('[KlineChart] 懒加载检查', {
              from,
              threshold,
              isLoading: isLoadingRef.current,
              hasEarliestTimestamp: !!earliestTimestampRef.current,
              currentDataLength: candlesRef.current.length,
              symbol: symbolRef.current,
              bar: barRef.current,
            });

            if (from !== null && from < threshold && !isLoadingRef.current && earliestTimestampRef.current) {
              console.log('[KlineChart] 触发懒加载历史数据', {
                from,
                threshold,
                earliestTimestamp: earliestTimestampRef.current,
                currentDataLength: candlesRef.current.length,
                symbol: symbolRef.current,
                bar: barRef.current,
              });

              // 记录当前可视区域的逻辑范围，用于更新后恢复位置
              const currentLogicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
              const oldDataLength = candlesRef.current.length;

              // 加载历史数据
              if (loadKlineDataRef.current) {
                console.log('[KlineChart] 开始加载历史数据', {
                  symbol: symbolRef.current,
                  bar: barRef.current,
                  oldDataLength,
                  currentLogicalRange,
                });
                loadKlineDataRef
                  .current(symbolRef.current, barRef.current, true)
                  .then(() => {
                    console.log('[KlineChart] 历史数据加载完成', {
                      newDataLength: candlesRef.current.length,
                      dataAdded: candlesRef.current.length - oldDataLength,
                    });
                    // 数据加载完成后，恢复可视区域位置
                    if (currentLogicalRange && chartRef.current) {
                      // 计算新数据添加后的逻辑位置偏移
                      const newDataLength = candlesRef.current.length;
                      const dataAdded = newDataLength - oldDataLength;

                      if (dataAdded > 0) {
                        const adjustedFrom = (currentLogicalRange.from || 0) + dataAdded;
                        const adjustedTo = (currentLogicalRange.to || 0) + dataAdded;

                        console.log('[KlineChart] 恢复可视区域位置', {
                          adjustedFrom,
                          adjustedTo,
                          dataAdded,
                          oldLogicalRange: currentLogicalRange,
                        });

                        // 恢复可视区域，保持用户看到的相对位置
                        setTimeout(() => {
                          if (chartRef.current) {
                            chartRef.current.timeScale().setVisibleLogicalRange({
                              from: adjustedFrom,
                              to: adjustedTo,
                            });
                          }
                        }, 50);
                      }
                    }
                  })
                  .catch((error) => {
                    console.error('[KlineChart] 历史数据加载失败', {
                      error: error.message,
                      symbol: symbolRef.current,
                      bar: barRef.current,
                    });
                  });
              } else {
                console.warn('[KlineChart] loadKlineDataRef.current 为空，无法加载历史数据');
              }
            } else {
              // 记录为什么不触发懒加载
              if (from !== null && from >= threshold) {
                console.log('[KlineChart] 懒加载检查：from >= threshold，不触发懒加载', { from, threshold });
              } else if (isLoadingRef.current) {
                console.log('[KlineChart] 懒加载检查：正在加载中，跳过');
              } else if (!earliestTimestampRef.current) {
                console.log('[KlineChart] 懒加载检查：没有最早时间戳，跳过');
              }
            }
          };

          chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRangeHandler);
          logicalRangeHandlerRef.current = logicalRangeHandler;

          // 响应式调整
          const handleResize = () => {
            if (chartContainerRef.current && chartRef.current) {
              const rect = chartContainerRef.current.getBoundingClientRect();
              const newWidth = rect.width || chartContainerRef.current.clientWidth;
              const newHeight = parseHeight(height);

              if (newWidth > 0 && newHeight > 0) {
                chartRef.current.applyOptions({
                  width: newWidth,
                  height: newHeight,
                });
              }
            }
          };

          window.addEventListener('resize', handleResize);

          return () => {
            window.removeEventListener('resize', handleResize);
          };
        } catch (error) {
          console.error('图表初始化失败:', error);
        }
      }

      return () => {
        clearTimeout(initTimer);
        if (chartRef.current && logicalRangeHandlerRef.current) {
          chartRef.current.timeScale().unsubscribeVisibleLogicalRangeChange(logicalRangeHandlerRef.current);
          logicalRangeHandlerRef.current = null;
        }
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
          candleSeriesRef.current = null;
          volumeSeriesRef.current = null;
          markersPluginRef.current = null;
          isInitializedRef.current = false;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme, height]);

    // 更新图表数据（使用防抖动策略）
    useEffect(() => {
      if (!candleSeriesRef.current) {
        // 如果图表还没初始化，等待一下再试
        if (candles.length > 0) {
          const timer = setTimeout(() => {
            if (candleSeriesRef.current && candles.length > 0) {
              console.log('[KlineChart] 图表初始化后设置数据', { count: candles.length });
              // 记录当前可视区域的逻辑范围（用于恢复位置）
              const currentLogicalRange = chartRef.current?.timeScale().getVisibleLogicalRange();

              candlesRef.current = candles;
              candleSeriesRef.current.setData(toCandlestickDataArray(candles));

              if (volumeSeriesRef.current) {
                if (showVolume) {
                  volumeSeriesRef.current.setData(toVolumeDataArray(candles));
                } else {
                  volumeSeriesRef.current.setData([]);
                }
              }

              // 恢复可视区域位置（防止视图跳转）
              if (chartRef.current && currentLogicalRange) {
                setTimeout(() => {
                  if (chartRef.current) {
                    chartRef.current.timeScale().setVisibleLogicalRange(currentLogicalRange);
                    if (autoScroll) {
                      chartRef.current.timeScale().scrollToRealTime();
                    }
                  }
                }, 50);
              } else if (chartRef.current) {
                setTimeout(() => {
                  if (chartRef.current) {
                    chartRef.current.timeScale().fitContent();
                    if (autoScroll) {
                      chartRef.current.timeScale().scrollToRealTime();
                    }
                  }
                }, 100);
              }
            }
          }, 200);
          return () => clearTimeout(timer);
        }
        return;
      }

      if (candles.length === 0) {
        console.log('[KlineChart] candles为空，跳过更新');
        return;
      }

      console.log('[KlineChart] 更新图表数据', {
        count: candles.length,
        isExternal: !!externalCandles,
        isInternal: !!internalCandles,
      });

      // 记录当前可视区域的逻辑范围（用于恢复位置）
      const currentLogicalRange = chartRef.current?.timeScale().getVisibleLogicalRange();
      const oldDataLength = candlesRef.current.length;
      const wasDataAppended =
        candles.length > oldDataLength &&
        oldDataLength > 0 &&
        candles.length > 0 &&
        candles[0].time === candlesRef.current[0]?.time;

      candlesRef.current = candles;
      const candleData = toCandlestickDataArray(candles);

      console.log('[KlineChart] 调用setData', { dataCount: candleData.length });
      candleSeriesRef.current.setData(candleData);

      if (volumeSeriesRef.current) {
        if (showVolume) {
          volumeSeriesRef.current.setData(toVolumeDataArray(candles));
        } else {
          volumeSeriesRef.current.setData([]);
        }
      }

      // 如果是在头部追加数据（历史数据），需要调整逻辑范围以保持可视位置
      if (wasDataAppended && currentLogicalRange && chartRef.current) {
        const dataAdded = candles.length - oldDataLength;
        if (dataAdded > 0) {
          const adjustedFrom = (currentLogicalRange.from || 0) + dataAdded;
          const adjustedTo = (currentLogicalRange.to || 0) + dataAdded;

          setTimeout(() => {
            if (chartRef.current) {
              chartRef.current.timeScale().setVisibleLogicalRange({
                from: adjustedFrom,
                to: adjustedTo,
              });
            }
          }, 50);
        }
      } else if (chartRef.current && candles.length > 0) {
        // 检查是否有待设置的视口范围（初始加载时）
        if (pendingVisibleRangeRef.current && !wasDataAppended) {
          const { candles: pendingCandles, sym, interval } = pendingVisibleRangeRef.current;
          // 确保数据已经设置完成后再设置视口
          setTimeout(() => {
            if (chartRef.current && isInitializedRef.current && setVisibleRangeAfterLoadRef.current) {
              setVisibleRangeAfterLoadRef.current(pendingCandles, sym, interval);
              pendingVisibleRangeRef.current = null; // 清除待设置的视口范围
              // 设置视口完成后，延迟重置初始加载标记，确保懒加载不会误触发
              setTimeout(() => {
                isInitialDataLoadRef.current = false;
              }, 500); // 设置视口后500ms才允许懒加载
            }
          }, 100);
        } else if (!wasDataAppended) {
          // 自动调整图表（数据更新时，但不是初始加载）
          setTimeout(() => {
            if (!chartRef.current) return;
            chartRef.current.timeScale().fitContent();
            if (autoScroll) {
              chartRef.current.timeScale().scrollToRealTime();
            }
          }, 100);
        }
      }
    }, [candles, showVolume, autoScroll, externalCandles, internalCandles]);

    // 更新标记
    useEffect(() => {
      if (markersPluginRef.current) {
        markersPluginRef.current.setMarkers(markers);
      }
    }, [markers]);

    // 格式化时间范围显示
    const formatTimeRange = (earliest: number, latest: number): string => {
      const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Shanghai',
        });
      };
      return `${formatDate(earliest)} 至 ${formatDate(latest)}`;
    };

    // 刷新K线数据
    const handleRefresh = useCallback(() => {
      console.log('[KlineChart] 点击刷新按钮', {
        isAnyLoading,
        internalLoading,
        externalCandles: !!externalCandles,
        autoLoad,
        symbol,
        bar,
        hasLoadKlineDataRef: !!loadKlineDataRef.current,
      });

      if (isAnyLoading || internalLoading) {
        console.log('[KlineChart] 正在加载中，跳过刷新');
        return; // 正在加载中，不执行刷新
      }

      if (!symbol || !bar) {
        console.warn('[KlineChart] 刷新失败：symbol或bar为空', { symbol, bar });
        return;
      }

      // 无论是否有外部数据，都尝试刷新
      // 重置状态，强制重新加载
      hasEmptyDataRef.current = false;
      lastLoadKeyRef.current = '';
      earliestTimestampRef.current = null; // 重置最早时间戳，重新加载最新数据

      // 重置时间范围相关状态，强制重新获取时间范围
      lastTimeRangeKeyRef.current = ''; // 重置时间范围缓存键，强制重新获取
      timeRangeLoadingRef.current = false; // 重置时间范围加载状态
      setTimeRange(null); // 清空当前时间范围，等待重新获取
      console.log('[KlineChart] 已重置时间范围状态，将重新获取最新时间范围');

      // 直接调用获取时间范围的接口
      const fetchTimeRange = async () => {
        timeRangeLoadingRef.current = true;
        lastTimeRangeKeyRef.current = `${symbol}-${bar}`;

        try {
          const range = await klineDataService.getKlineTimeRange(symbol, bar);
          console.log('[KlineChart] 刷新后获取到时间范围:', range);
          if (range.hasData && range.earliestTime && range.latestTime) {
            setTimeRange({
              earliest: range.earliestTime,
              latest: range.latestTime,
            });
            console.log('[KlineChart] 刷新后设置时间范围:', { earliest: range.earliestTime, latest: range.latestTime });
          } else {
            console.log('[KlineChart] 刷新后无数据或时间范围无效');
            setTimeRange(null);
          }
        } catch (error: any) {
          console.error('[KlineChart] 刷新后获取K线时间范围失败:', error);
          setTimeRange(null);
        } finally {
          timeRangeLoadingRef.current = false;
        }
      };
      fetchTimeRange();

      // 优先使用ref，如果没有则直接调用函数
      // 传入 forceRefresh=true 强制刷新，即使有外部数据也执行
      if (loadKlineDataRef.current) {
        console.log('[KlineChart] 通过ref调用loadKlineData刷新（强制刷新）');
        loadKlineDataRef.current(symbol, bar, false, true);
      } else {
        console.log('[KlineChart] 直接调用loadKlineData刷新（强制刷新）');
        loadKlineData(symbol, bar, false, true);
      }
    }, [symbol, bar, internalLoading, isAnyLoading, loadKlineData, autoLoad, externalCandles]);

    return (
      <div className="w-full flex flex-col">
        <div
          ref={chartContainerRef}
          className="w-full flex-1 overflow-visible relative"
          style={{
            position: 'relative',
            zIndex: 20,
            backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
            paddingBottom: '0px',
            height: typeof height === 'number' ? `${height}px` : height,
          }}
        >
          {/* 刷新按钮 */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[KlineChart] 刷新按钮被点击');
              handleRefresh();
            }}
            disabled={loading || internalLoading}
            className="absolute top-2 right-2 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            title="刷新K线数据"
            style={{
              backdropFilter: 'blur(4px)',
              pointerEvents: 'auto',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 ${theme === 'dark' ? 'text-white' : 'text-gray-800'} ${loading || internalLoading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-30">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}
          {internalError && !loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 z-30">
              <div className="text-red-300 text-sm">{internalError}</div>
            </div>
          )}
        </div>
        {timeRange && (
          <div
            className="text-xs text-gray-400 text-center px-2 relative z-10"
            style={{ backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff' }}
          >
            数据时间范围: {formatTimeRange(timeRange.earliest, timeRange.latest)}
          </div>
        )}
      </div>
    );
  },
);

KlineChart.displayName = 'KlineChart';

export default KlineChart;
